import React, { useEffect } from "react";
import Header from "../Components/Header";
import Newsletter from "../Components/Newsletter";
import Footer from "../Components/Footer";
import { useParams } from "react-router-dom";
import TeamData from "../Pages/TeamData.json";
import {
  TeamDetailCardLayout,
  TeamDetailMinimalLayout,
  TeamDetailSplitLayout,
  TeamDetailHeroLayout,
  TeamDetailGridLayout,
  TeamDetailSidebarLayout,
  TeamDetailTimelineLayout,
  TeamDetailGlassLayout
} from "./TeamDetailLayouts";
import { TeamDetailModernLayout } from "./TeamDetailModernLayout";
import {
  TeamDetailModernCompact,
  TeamDetailModernCentered,
  TeamDetailModernSplit,
  TeamDetailModernMinimal,
  TeamDetailModernCardGrid
} from "./TeamDetailModernVariations";
import {
  TeamDetailAnimatedHero,
  TeamDetailGlassParallax,
  TeamDetail3DCards
} from "./TeamDetailAnimatedLayouts";

// Default layout configuration
const defaultLayoutOptions = {
  // Layout Style - Choose a visual layout style
  layoutStyle: "default", // "default" | "card" | "minimal" | "split" | "hero" | "grid" | "sidebar" | "timeline" | "glass" | "modern" | "modernCompact" | "modernCentered" | "modernSplit" | "modernMinimal" | "modernCardGrid" | "animatedHero" | "glassParallax" | "cards3D"
  
  // Banner/Header section
  showBanner: true,
  bannerImage: "/assets/img/contactus-banner.jpg",
  bannerOverlay: "8",
  
  // Main content sections (only used in "default" layout)
  showImage: true,
  showBasicInfo: true, // Name, role, description
  showActivitiesSkills: true,
  showProfessionalSkills: true,
  
  // Footer sections
  showNewsletter: true,
  showFooter: true,
  showHeader: true, // Show/hide Header component
  
  // Layout variations (only used in "default" layout)
  imagePosition: "left", // "left" | "right" | "top" | "center"
  skillsLayout: "side-by-side", // "side-by-side" | "stacked" | "single-column"
};

// Layout component mapping
const layoutComponents = {
  card: TeamDetailCardLayout,
  minimal: TeamDetailMinimalLayout,
  split: TeamDetailSplitLayout,
  hero: TeamDetailHeroLayout,
  grid: TeamDetailGridLayout,
  sidebar: TeamDetailSidebarLayout,
  timeline: TeamDetailTimelineLayout,
  glass: TeamDetailGlassLayout,
  modern: TeamDetailModernLayout, // Original modern layout
  modernCompact: TeamDetailModernCompact, // Sidebar style
  modernCentered: TeamDetailModernCentered, // All centered
  modernSplit: TeamDetailModernSplit, // Two column
  modernMinimal: TeamDetailModernMinimal, // Ultra minimal
  modernCardGrid: TeamDetailModernCardGrid, // Card grid
  animatedHero: TeamDetailAnimatedHero, // Animated hero with floating cards
  glassParallax: TeamDetailGlassParallax, // Glassmorphism with parallax
  cards3D: TeamDetail3DCards, // 3D tilt cards
};

function TeamDetailPage({ layoutOptions = {} }) {
  const { slug } = useParams(); // Get the slug from the URL

  const findTeamMemberBySlug = (slug) => {

    const leadershipMember = TeamData.leadership_team?.team_member_detail?.find(
      (member) => member.slug === slug
    );
  
    const operationMember = TeamData.operation_team?.team_member_detail?.find(
      (member) => member.slug === slug
    );

    // No detail page for advisory board for now – commented out
    // const advisoryMember = TeamData.advisory_board?.find(
    //   (member) => member.slug === slug
    // );

    if (leadershipMember) {
      return { member: leadershipMember, teamType: 'leadership' }; 
    } else if (operationMember) {
      return { member: operationMember, teamType: 'operation' }; 
    }
    // } else if (advisoryMember) {
    //   return { member: advisoryMember, teamType: 'advisory' };
    // }
  
    
    return null;
  };

  const teamMemberData = findTeamMemberBySlug(slug);
  const teamMember = teamMemberData?.member || null;
  const isLeadershipTeam = teamMemberData?.teamType === 'leadership';
  // const isAdvisoryTeam = teamMemberData?.teamType === 'advisory'; // No detail page for advisory board
  const useLeadershipStyle = isLeadershipTeam; // was: isLeadershipTeam || isAdvisoryTeam

  console.log("teamMember", teamMember);

  // Scroll to top on page load, especially important for Leadership Team pages
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Auto-detect layout for Leadership Team if no explicit layoutStyle is provided
  const finalLayoutStyle = layoutOptions.layoutStyle || (useLeadershipStyle ? "cards3D" : defaultLayoutOptions.layoutStyle);
  const finalOptions = { 
    ...defaultLayoutOptions, 
    ...layoutOptions,
    layoutStyle: finalLayoutStyle
  };

  // Get the layout component based on layoutStyle
  const LayoutComponent = layoutComponents[finalOptions.layoutStyle] || null;

  // If using a custom layout style, render it
  if (LayoutComponent && finalOptions.layoutStyle !== "default") {
    return (
      <>
        {finalOptions.showHeader && <Header />}
        <div className="main">
          {/* Banner Section - hide for Leadership + Advisory */}
          {finalOptions.showBanner && !useLeadershipStyle && (
            <section
              className="page-header-section ptb-100 bg-image"
              image-overlay={finalOptions.bannerOverlay}
            >
              <div
                className="background-image-wraper"
                style={{
                  background: `url('${finalOptions.bannerImage}')`,
                  opacity: "1",
                }}
              ></div>
              <div className="container">
                <div className="row align-items-center">
                  <div className="col-md-9 col-lg-7">
                    <div className="page-header-content text-white pt-4">
                      <h1 className="text-white mb-0">{teamMember?.name || "Team Member"}</h1>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Custom Layout Component */}
          <LayoutComponent teamMember={teamMember} />
        </div>

        {/* Newsletter */}
        {finalOptions.showNewsletter && <Newsletter />}
        
        {/* Footer */}
        {finalOptions.showFooter && <Footer />}
      </>
    );
  }

  // Default layout with all sections
  // Determine image column order based on position
  const getImageColumnClass = () => {
    if (finalOptions.imagePosition === "right") return "col-md-12 col-sm-12 col-lg-6 order-lg-2";
    if (finalOptions.imagePosition === "top") return "col-md-12 col-sm-12 col-lg-12 mb-4";
    if (finalOptions.imagePosition === "center") return "col-md-12 col-sm-12 col-lg-8 mx-auto mb-4";
    return "col-md-12 col-sm-12 col-lg-6"; // default left
  };

  const getContentColumnClass = () => {
    if (finalOptions.imagePosition === "right") return "col-md-12 col-sm-12 col-lg-6 order-lg-1";
    if (finalOptions.imagePosition === "top" || finalOptions.imagePosition === "center") return "col-md-12 col-sm-12 col-lg-12";
    return "col-md-12 col-sm-12 col-lg-6"; // default left
  };

  const getSkillsColumnClass = () => {
    if (finalOptions.skillsLayout === "single-column") return "col-md-12 col-sm-12 col-12";
    if (finalOptions.skillsLayout === "stacked") return "col-md-12 col-sm-12 col-12 mb-4";
    return "col-md-6 col-sm-6 col-12"; // default side-by-side
  };

  return (
    <>
      {finalOptions.showHeader && <Header />}
      <div className="main">
        {/* Banner Section - hide for Leadership + Advisory */}
        {finalOptions.showBanner && !useLeadershipStyle && (
          <section
            className="page-header-section ptb-100 bg-image"
            image-overlay={finalOptions.bannerOverlay}
          >
            <div
              className="background-image-wraper"
              style={{
                background: `url('${finalOptions.bannerImage}')`,
                opacity: "1",
              }}
            ></div>
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-9 col-lg-7">
                  <div className="page-header-content text-white pt-4">
                    <h1 className="text-white mb-0">{teamMember?.name || "Team Member"}</h1>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Main Content Section */}
        <section className="team-single-section ptb-100">
          <div className="container">
            {/* Image and Basic Info */}
            {(finalOptions.showImage || finalOptions.showBasicInfo) && (
              <div className={`row ${finalOptions.imagePosition === "top" || finalOptions.imagePosition === "center" ? "" : "align-items-center"}`}>
                {/* Image */}
                {finalOptions.showImage && (
                  <div className={getImageColumnClass()}>
                    <div className="team-single-img">
                      <div
                        className="team-detail-image img-fluid m-auto"
                        style={{
                          backgroundImage: `url(${teamMember?.image})`,
                        }}
                        alt="member"
                      ></div>
                    </div>
                  </div>
                )}

                {/* Basic Info */}
                {finalOptions.showBasicInfo && (
                  <div className={getContentColumnClass()}>
                    <div className="team-single-text">
                      <div className="team-name mb-4">
                        <h4 className="mb-1">{teamMember?.name}</h4>
                        <span>{teamMember?.role || "--"}</span>
                      </div>

                      <div className="text-content mt-20">
                        <p>{teamMember?.about || teamMember?.description || "--"}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Skills Sections */}
            {(finalOptions.showActivitiesSkills || finalOptions.showProfessionalSkills) && (
              <div className={`row mt-5 ${finalOptions.skillsLayout === "stacked" ? "" : ""}`}>
                {/* Activities And Skills */}
                {finalOptions.showActivitiesSkills && teamMember?.activities_skills?.length > 0 && (
                  <div className={getSkillsColumnClass()}>
                    <div className="section-heading">
                      <h5>Activities And Skills</h5>
                      <div className="section-heading-line-left"></div>
                    </div>
                    <ul className="check-list-wrap pt-3">
                      {teamMember.activities_skills.map((data, index) => (
                        <li key={index}>
                          <strong>{data.heading}</strong> – {data.desc}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Professional Skills */}
                {finalOptions.showProfessionalSkills && teamMember?.professional_skills?.length > 0 && (
                  <div className={getSkillsColumnClass()}>
                    <div className={`section-heading ${finalOptions.skillsLayout === "side-by-side" ? "mt-40" : ""}`}>
                      <h5>Professional Skills</h5>
                      <div className="section-heading-line-left"></div>
                    </div>
                    <div className="skill-content-wrap">
                      <p>
                        Holisticly whiteboard low-risk high-yield e-services
                        vis-a-vis user-centric data. Competently benchmark
                        communities whereas interactive.
                      </p>
                      {teamMember.professional_skills.map((data, index) => (
                        <div key={index} className="progress-item">
                          <div className="progress-title">
                            <h6>
                              {data.heading}
                              <span className="float-right">
                                <span className="progress-number">{data?.percentage}</span>
                              </span>
                            </h6>
                          </div>
                          <div className="progress">
                            <span style={{ width: data?.percentage }}>
                              <span className="progress-line"></span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Newsletter */}
      {finalOptions.showNewsletter && <Newsletter />}
      
      {/* Footer */}
      {finalOptions.showFooter && <Footer />}
    </>
  );
}

export default TeamDetailPage;
